import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateUnit } from '@/lib/admin/units';
import { useToast } from '@/hooks/use-toast';
import type { Tables } from '@/integrations/supabase/types';
import { debounce } from 'lodash-es';

type PropertyUnit = Tables<'property_units'>;

interface UnitFormData {
  availability: 'available' | 'reserved' | 'sold';
  view_type: 'lagoon' | 'canal';
  price: string;
  show_price: boolean;
  is_published: boolean;
  matterport_url: string;
  kuula_url: string;
  images: string[];
  floor_plan_images: string[];
  render_images: string[];
  render_rotation_angle: number;
  architecture_drawing_id: string | null;
}

interface UseUnitEditReturn {
  formData: UnitFormData;
  isLoading: boolean;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  saveStatus: 'saved' | 'saving' | 'unsaved';
  updateFormData: (field: keyof UnitFormData, value: UnitFormData[keyof UnitFormData]) => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
}

export function useUnitEdit(unit: PropertyUnit | null): UseUnitEditReturn {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [formData, setFormData] = useState<UnitFormData>({
    availability: 'available',
    view_type: 'lagoon',
    price: '',
    show_price: true,
    is_published: true,
    matterport_url: '',
    kuula_url: '',
    images: [],
    floor_plan_images: [],
    render_images: [],
    render_rotation_angle: 0,
    architecture_drawing_id: null,
  });

  // Initialize form data when unit loads
  useEffect(() => {
    if (unit) {
      setFormData({
        availability: unit.availability,
        view_type: unit.view_type,
        price: unit.price?.toString() || '',
        show_price: unit.show_price,
        is_published: unit.is_published,
        matterport_url: unit.matterport_url || '',
        kuula_url: unit.kuula_url || '',
        images: unit.images || [],
        floor_plan_images: unit.floor_plan_images || [],
        render_images: unit.render_images || [],
        render_rotation_angle: unit.render_rotation_angle ?? 0,
        architecture_drawing_id: unit.architecture_drawing_id ?? null,
      });
    }
  }, [unit]);

  // Auto-save with debounce
  const performAutoSave = useCallback(async () => {
    if (!unit) return;

    setSaveStatus('saving');
    setIsSaving(true);

    try {
      const priceValue = formData.price.trim() === '' ? null : parseFloat(formData.price);
      if (formData.price.trim() !== '' && (isNaN(priceValue!) || priceValue! < 0)) {
        setSaveStatus('unsaved');
        setIsSaving(false);
        return;
      }

      const updates = {
        availability: formData.availability,
        view_type: formData.view_type,
        price: priceValue,
        show_price: formData.show_price,
        is_published: formData.is_published,
        matterport_url: formData.matterport_url.trim() || null,
        kuula_url: formData.kuula_url.trim() || null,
        images: formData.images,
        floor_plan_images: formData.floor_plan_images,
        render_images: formData.render_images,
        render_rotation_angle: formData.render_rotation_angle,
        architecture_drawing_id: formData.architecture_drawing_id,
      };

      const result = await updateUnit(unit.id, updates);

      if (result.success) {
        setSaveStatus('saved');
        setHasUnsavedChanges(false);
      } else {
        setSaveStatus('unsaved');
      }
    } catch (error) {
      console.error('Auto-save error:', error);
      setSaveStatus('unsaved');
    } finally {
      setIsSaving(false);
    }
  }, [unit, formData]);

  const autoSave = useRef(debounce(() => {
    performAutoSave();
  }, 2000)).current;

  const updateFormData = useCallback((field: keyof UnitFormData, value: UnitFormData[keyof UnitFormData]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
    setSaveStatus('unsaved');
    
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Trigger debounced auto-save
    autoSave();
  }, [autoSave]);

  const handleSave = useCallback(async () => {
    if (!unit) return;

    // Clear auto-save timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    setIsSaving(true);
    setSaveStatus('saving');

    try {
      // Validate price
      const priceValue = formData.price.trim() === '' ? null : parseFloat(formData.price);
      if (formData.price.trim() !== '' && (isNaN(priceValue!) || priceValue! < 0)) {
        toast({
          title: "Invalid Price",
          description: "Please enter a valid price.",
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }

      const updates = {
        availability: formData.availability,
        view_type: formData.view_type,
        price: priceValue,
        show_price: formData.show_price,
        is_published: formData.is_published,
        matterport_url: formData.matterport_url.trim() || null,
        kuula_url: formData.kuula_url.trim() || null,
        images: formData.images,
        floor_plan_images: formData.floor_plan_images,
        render_images: formData.render_images,
        render_rotation_angle: formData.render_rotation_angle,
        architecture_drawing_id: formData.architecture_drawing_id,
      };

      const result = await updateUnit(unit.id, updates);

      if (result.success) {
        toast({
          title: "Success",
          description: "Unit updated successfully.",
        });
        setHasUnsavedChanges(false);
        setSaveStatus('saved');
        navigate('/admin/units');
      } else {
        setSaveStatus('unsaved');
        toast({
          title: "Error",
          description: result.error || "Failed to update unit.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error updating unit:', error);
      setSaveStatus('unsaved');
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }, [unit, formData, navigate, toast]);

  // Cleanup on unmount
  useEffect(() => {
    const timeoutId = autoSaveTimeoutRef.current;
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!confirmed) return;
    }
    navigate('/admin/units');
  }, [hasUnsavedChanges, navigate]);

  return {
    formData,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    saveStatus,
    updateFormData,
    handleSave,
    handleCancel,
  };
}
